import type { Control, FieldErrors } from 'react-hook-form'
import { Controller, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  Grid,
  HStack,
  IconButton,
  Input,
  Stack,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular, FaXmarkRegular } from '@enechain/ecloud-icons'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import type { TradingPartnerFormInput } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/schema'

// アカウント配下の連絡先は平日昼間 / 休日・夜間の 2 種別を持つ。 schema 側の
// field 名 (weekdayContacts / holidayContacts) と 1:1 で対応させる。
type ContactKind = 'weekdayContacts' | 'holidayContacts'

type Props = {
  control: Control<TradingPartnerFormInput>
  errors: FieldErrors<TradingPartnerFormInput>
  /** 親アカウントの index (= accounts.${accountIndex}.${kind} の path を構成)。 */
  accountIndex: number
  /** 連絡先の種別 (= 平日昼間 / 休日・夜間)。 schema field 名と一致させる。 */
  kind: ContactKind
  /** セクション見出し + 追加ボタンの accessible name に使う日本語ラベル。 */
  label: string
}

/**
 * アカウント配下の連絡先 (平日昼間 / 休日・夜間) 編集セクション。
 *
 * design/admin/occto.html #tp-edit-modal の `.tp-contact-row` (grid-template-columns:
 * 1fr 1fr 1fr 1.6fr auto) 準拠: 連絡先名称 / 担当者 / 電話番号 / メール / 除去ボタンを
 * 1 行に横並びさせる (= 旧: 縦積みカード + 全幅「削除」ボタン)。
 *
 * `accounts.${accountIndex}.${kind}` の nested useFieldArray を管理し、 各エントリ
 * (連絡先名称 / 担当者 / 電話番号 / メール) の追加・編集・削除を行う。 連絡先名称
 * (department) と電話番号 (phone) は必須、 担当者 (name) とメール (email) は任意
 * (= schema の contactEntryInputSchema と整合)。 連絡先は 0 件でもよい
 * (= proto は repeated で min_items 制約なし)。
 *
 * AC-03 (spec AC-T04): 連絡先の除去は確認ダイアログを経由せず即時実行する
 * (= アカウント削除 (AC-04) と異なり、 単独の連絡先除去は確認不要。 design の
 * aria-label="除去" も確認ダイアログなしの単発操作であることを示す)。
 *
 * mock-app OcctoMaster.razor の取引先編集モーダル (平日昼間 / 休日・夜間 連絡先) に準拠。
 * 旧 #419 (ContactEntry 編集 UI 残論点) の Phase 2 を mock-app 仕様で確定して実装。
 */
export const ContactEntryFields = ({
  control,
  errors,
  accountIndex,
  kind,
  label,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({
    control,
    name: `accounts.${accountIndex}.${kind}`,
  })

  const entryErrors = errors.accounts?.[accountIndex]?.[kind]

  return (
    <Box>
      {/* sub-header: ラベル + 右端「+ 追加」(design: .occto-sub-header) */}
      <HStack
        justify="space-between"
        marginBottom={2}
        paddingTop={2}
        borderColor="Border.lowEmphasis"
        borderTopWidth="1px"
      >
        <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
          {label}
        </Text>
        <TertiaryButton
          aria-label={t(
            'page:adminOcctoMaster.tradingPartner.form.addSectionAriaLabel',
            {
              label,
            },
          )}
          onClick={() => {
            append({ department: '', name: '', phone: '', email: '' })
          }}
          size="sm"
        >
          <FaPlusRegular />
          {t('page:adminOcctoMaster.tradingPartner.form.add')}
        </TertiaryButton>
      </HStack>
      <Stack gap={2}>
        {fields.map((field, entryIndex) => {
          const fieldErrors = entryErrors?.[entryIndex]
          const idPrefix = `account-${accountIndex}-${kind}-${entryIndex}`
          return (
            <Grid
              key={field.id}
              alignItems="end"
              gridGap={2}
              gridTemplateColumns="1fr 1fr 1fr 1.6fr auto"
            >
              {/* department (連絡先名称、 必須) */}
              <Controller
                control={control}
                name={`accounts.${accountIndex}.${kind}.${entryIndex}.department`}
                render={({ field: f }) => (
                  <FieldRoot
                    alignItems="stretch"
                    invalid={fieldErrors?.department !== undefined}
                  >
                    <FieldLabel htmlFor={`${idPrefix}-department`}>
                      {t(
                        'page:adminOcctoMaster.tradingPartner.form.contactDepartment',
                      )}
                    </FieldLabel>
                    <Input
                      {...f}
                      id={`${idPrefix}-department`}
                      placeholder={t(
                        'page:adminOcctoMaster.tradingPartner.form.contactDepartmentPlaceholder',
                      )}
                    />
                    {fieldErrors?.department !== undefined && (
                      <FieldErrorText>
                        {fieldErrors.department.message}
                      </FieldErrorText>
                    )}
                  </FieldRoot>
                )}
              />
              {/* name (担当者、 任意。 入力時は max 30 rune を検証するため error 表示あり) */}
              <Controller
                control={control}
                name={`accounts.${accountIndex}.${kind}.${entryIndex}.name`}
                render={({ field: f }) => (
                  <FieldRoot
                    alignItems="stretch"
                    invalid={fieldErrors?.name !== undefined}
                  >
                    <FieldLabel htmlFor={`${idPrefix}-name`}>
                      {t(
                        'page:adminOcctoMaster.tradingPartner.form.contactName',
                      )}
                    </FieldLabel>
                    <Input
                      {...f}
                      id={`${idPrefix}-name`}
                      value={f.value ?? ''}
                    />
                    {fieldErrors?.name !== undefined && (
                      <FieldErrorText>
                        {fieldErrors.name.message}
                      </FieldErrorText>
                    )}
                  </FieldRoot>
                )}
              />
              {/* phone (電話番号、 必須) */}
              <Controller
                control={control}
                name={`accounts.${accountIndex}.${kind}.${entryIndex}.phone`}
                render={({ field: f }) => (
                  <FieldRoot
                    alignItems="stretch"
                    invalid={fieldErrors?.phone !== undefined}
                  >
                    <FieldLabel htmlFor={`${idPrefix}-phone`}>
                      {t(
                        'page:adminOcctoMaster.tradingPartner.form.contactPhone',
                      )}
                    </FieldLabel>
                    <Input
                      {...f}
                      id={`${idPrefix}-phone`}
                      placeholder={t(
                        'page:adminOcctoMaster.tradingPartner.form.contactPhonePlaceholder',
                      )}
                    />
                    {fieldErrors?.phone !== undefined && (
                      <FieldErrorText>
                        {fieldErrors.phone.message}
                      </FieldErrorText>
                    )}
                  </FieldRoot>
                )}
              />
              {/* email (メール、 任意。 入力時は RFC 形式を検証するため error 表示あり) */}
              <Controller
                control={control}
                name={`accounts.${accountIndex}.${kind}.${entryIndex}.email`}
                render={({ field: f }) => (
                  <FieldRoot
                    alignItems="stretch"
                    invalid={fieldErrors?.email !== undefined}
                  >
                    <FieldLabel htmlFor={`${idPrefix}-email`}>
                      {t(
                        'page:adminOcctoMaster.tradingPartner.form.contactEmail',
                      )}
                    </FieldLabel>
                    <Input
                      {...f}
                      id={`${idPrefix}-email`}
                      value={f.value ?? ''}
                    />
                    {fieldErrors?.email !== undefined && (
                      <FieldErrorText>
                        {fieldErrors.email.message}
                      </FieldErrorText>
                    )}
                  </FieldRoot>
                )}
              />
              {/* 除去 (design: .ecl-icon-btn 準拠 = border は neutral 1px、 icon 色のみ
                  negative。 IconButton の variant="outline" 既定は borderColor が
                  currentColor 由来のため、 border/icon 色を分離するために両方明示する
                  (= RowActionButtons.tsx の EditIconButton/DeleteIconButton と同方針)。
                  40x40 相当 (size 既定で 40px)、 確認ダイアログなし) */}
              <Tooltip
                content={t(
                  'page:adminOcctoMaster.tradingPartner.form.removeContact',
                )}
              >
                <IconButton
                  color="Text.negativeMid"
                  borderWidth="1px"
                  borderColor="Border.midEmphasis"
                  aria-label={t(
                    'page:adminOcctoMaster.tradingPartner.form.removeContact',
                  )}
                  onClick={() => {
                    remove(entryIndex)
                  }}
                  variant="outline"
                >
                  <FaXmarkRegular />
                </IconButton>
              </Tooltip>
            </Grid>
          )
        })}
      </Stack>
    </Box>
  )
}
